import React, { useState, useEffect } from "react";
import "../styles/Chapters.css";
import axiosInstance from "../api/axiosInterceptor.js";
import SyncLoader from "../components/Loader";
import { formatDate, formatDateTime } from "../utils/formatDate";

const formatEuroAmount = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
  }).format(numericValue);
};

const isActiveUser = (value) => {
  if (value === false || value === 0 || value === "false" || value === "False") return false;
  return true;
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editType, setEditType] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [searchFilters, setSearchFilters] = useState({
    full_name: "",
    email: "",
    codice_fiscale: "",
    phone: "",
  });
  const [accountFilter, setAccountFilter] = useState("all");
  const [registrationFilter, setRegistrationFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: "registration", direction: "desc" });
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8; // Adjustable rows per page

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get("/admin/users/");
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
  };

  const handleBack = () => {
    setSelectedUser(null);
  };

  const handleEdit = (item, type) => {
    setEditItem(item);
    setEditType(type);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setEditItem(null);
    setEditType("");
    setIsModalOpen(false);
  };

  const handleViewPaymentDetails = (user) => {
    setSelectedPayments(user);
    setIsPaymentModalOpen(true);
  };

  const handlePaymentModalClose = () => {
    setSelectedPayments(null);
    setIsPaymentModalOpen(false);
  };

  const handleSaveEdit = async (updatedItem) => {
    setIsLoading(true);
    try {
      if (editType === "user") {
        await axiosInstance.patch(`/admin/users/${updatedItem.id}/`, updatedItem);
        const normalizedUser = {
          ...updatedItem,
          is_active: isActiveUser(updatedItem.is_active),
        };
        setUsers((prev) =>
          prev.map((user) =>
            user.id === updatedItem.id ? { ...user, ...normalizedUser } : user
          )
        );
      } else if (editType === "registration") {
        //const dataa = await axiosInstance.patch(`/course/registration/${updatedItem.id}/`, updatedItem);
        if (selectedUser) {
          const updatedUser = {
            ...selectedUser,
            registrations: selectedUser.registrations.map((reg) =>
              reg.id === updatedItem.id ? updatedItem : reg
            ),
          };
          setUsers((prev) =>
            prev.map((user) => (user.id === updatedUser.id ? updatedUser : user))
          );
          setSelectedUser(updatedUser);
        }
      }

      handleModalClose();
      setSuccessMessage("Edited successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Failed to save edit:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async (user) => {
    const nextStatus = !isActiveUser(user.is_active);
    setIsLoading(true);
    try {
      await axiosInstance.patch(`/admin/users/${user.id}/`, { is_active: nextStatus });
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, is_active: nextStatus } : u))
      );
      if (selectedUser && selectedUser.id === user.id) {
        setSelectedUser((prev) => ({ ...prev, is_active: nextStatus }));
      }
    } catch (error) {
      console.error("Failed to update user status:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleFilterChange = (key, value) => {
    setSearchFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchFilters({
      full_name: "",
      email: "",
      codice_fiscale: "",
      phone: "",
    });
    setAccountFilter("all");
    setRegistrationFilter("all");
    setSortConfig({ key: "registration", direction: "desc" });
    setCurrentPage(1);
  };

  const filteredUsers = React.useMemo(() => {
    return users.filter((user) => {
      const fullName = (user.full_name || "").toLowerCase();
      const email = (user.email || "").toLowerCase();
      const codice = (user.codice_fiscale || "").toLowerCase();
      const phone = (user.phone || "").toLowerCase();
      const nameQuery = searchFilters.full_name.toLowerCase();
      const emailQuery = searchFilters.email.toLowerCase();
      const codiceQuery = searchFilters.codice_fiscale.toLowerCase();
      const phoneQuery = searchFilters.phone.toLowerCase();
      const isActive = isActiveUser(user.is_active);
      const isRegistered = user.registrations && user.registrations.length > 0;

      const accountMatches =
        accountFilter === "all" ||
        (accountFilter === "active" && isActive) ||
        (accountFilter === "deleted" && !isActive);
      const registrationMatches =
        registrationFilter === "all" ||
        (registrationFilter === "paid" && isRegistered) ||
        (registrationFilter === "unpaid" && !isRegistered);

      return (
        fullName.includes(nameQuery) &&
        email.includes(emailQuery) &&
        codice.includes(codiceQuery) &&
        phone.includes(phoneQuery) &&
        accountMatches &&
        registrationMatches
      );
    });
  }, [users, searchFilters, accountFilter, registrationFilter]);

  // sort based on configuration
  const sortedUsers = React.useMemo(() => {
    if (!sortConfig.key) return filteredUsers;
    const sorted = [...filteredUsers].sort((a, b) => {
      let aVal = a[sortConfig.key] || "";
      let bVal = b[sortConfig.key] || "";

      if (sortConfig.key === "registration") {
        aVal = a.registrations && a.registrations.length > 0 ? 1 : 0;
        bVal = b.registrations && b.registrations.length > 0 ? 1 : 0;
      }

      if (sortConfig.key === "is_active") {
        aVal = isActiveUser(a.is_active) ? 1 : 0;
        bVal = isActiveUser(b.is_active) ? 1 : 0;
      }

      if (typeof aVal === "string") {
        return aVal.localeCompare(bVal);
      }
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    });
    if (sortConfig.direction === "desc") sorted.reverse();
    return sorted;
  }, [filteredUsers, sortConfig]);

    // Pagination logic
  const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = sortedUsers.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="chapter-container user-management-page">
      {isLoading && <SyncLoader />}
      {!selectedUser ? (
        <>
          <h1>👥 User Management</h1>

          <div className="user-controls-panel">
            <div className="user-controls-grid">
              <input
                type="text"
                placeholder="Search full name"
                value={searchFilters.full_name}
                onChange={(e) => handleFilterChange("full_name", e.target.value)}
              />
              <input
                type="text"
                placeholder="Search email"
                value={searchFilters.email}
                onChange={(e) => handleFilterChange("email", e.target.value)}
              />
              <input
                type="text"
                placeholder="Search codice fiscale"
                value={searchFilters.codice_fiscale}
                onChange={(e) => handleFilterChange("codice_fiscale", e.target.value)}
              />
              <input
                type="text"
                placeholder="Search phone"
                value={searchFilters.phone}
                onChange={(e) => handleFilterChange("phone", e.target.value)}
              />
              <select value={accountFilter} onChange={(e) => { setAccountFilter(e.target.value); setCurrentPage(1); }}>
                <option value="all">All Accounts</option>
                <option value="active">Only Active</option>
                <option value="deleted">Only Deleted</option>
              </select>
              <select value={registrationFilter} onChange={(e) => { setRegistrationFilter(e.target.value); setCurrentPage(1); }}>
                <option value="all">All Registrations</option>
                <option value="paid">Only Paid</option>
                <option value="unpaid">Only Unpaid</option>
              </select>
              <select
                value={sortConfig.key}
                onChange={(e) => setSortConfig((prev) => ({ ...prev, key: e.target.value }))}
              >
                <option value="full_name">Sort: Full Name</option>
                <option value="email">Sort: Email</option>
                <option value="codice_fiscale">Sort: Codice Fiscal</option>
                <option value="phone">Sort: Phone</option>
                <option value="registration">Sort: Registration</option>
                <option value="is_active">Sort: Account Status</option>
                <option value="id">Sort: ID</option>
              </select>
              <button
                type="button"
                className="open-btn"
                onClick={() =>
                  setSortConfig((prev) => ({
                    ...prev,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                  }))
                }
              >
                Order: {sortConfig.direction === "asc" ? "A-Z" : "Z-A"}
              </button>
              <button type="button" className="cancel-btn" onClick={clearFilters}>
                Reset
              </button>
            </div>
          </div>

          <div className="chapter-table-wrapper user-management-table-wrapper">
            {successMessage && (
              <div
                style={{
                  backgroundColor: "#d4edda",
                  color: "#155724",
                  padding: "10px",
                  borderRadius: "5px",
                  marginBottom: "10px",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                {successMessage}
              </div>
            )}

            <table className="chapter-table user-management-table desktop-only-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Codice Fiscal</th>
                  <th>Phone</th>
                  <th>Registration Status</th>
                  <th>Account Status</th>
                  <th>Account Action</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user, index) => {
                  const isRegistered = user.registrations.length > 0;
                  const isActive = isActiveUser(user.is_active);
                  return (
                    <tr key={user.id}>
                      <td>{startIndex + index + 1}</td>
                      <td>{user.full_name}</td>
                      <td>{user.email}</td>
                      <td>{user.codice_fiscale}</td>
                      <td>{user.phone || "N/A"}</td>
                      <td>
                        <span className={`status-pill ${isRegistered ? "status-paid" : "status-unpaid"}`}>
                          {isRegistered ? "Paid" : "Unpaid"}
                        </span>
                      </td>
                      <td>
                        <span className={`status-pill ${isActive ? "status-active" : "status-inactive"}`}>
                          {isActive ? "Active" : "Deleted"}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`user-status-btn ${isActive ? "status-delete" : "status-restore"}`}
                          onClick={() => handleToggleActive(user)}
                        >
                          {isActive ? "Delete" : "Restore"}
                        </button>
                      </td>
                      <td className="action-cell">
                        <button className="edit-btn" onClick={() => handleEdit(user, "user")}>
                          Edit User
                        </button>
                        {isRegistered && (
                          <button className="open-btn" onClick={() => handleViewDetails(user)}>
                            View Details
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="user-mobile-list">
              {paginatedUsers.map((user, index) => {
                const isRegistered = user.registrations.length > 0;
                const isActive = isActiveUser(user.is_active);
                return (
                  <article key={user.id} className="user-mobile-card">
                    <div className="user-mobile-card-header">
                      <div>
                        <p className="user-mobile-idx">#{startIndex + index + 1}</p>
                        <h3 className="user-mobile-name">{user.full_name || "N/A"}</h3>
                      </div>
                      <div className="user-mobile-badges">
                        <span className={`status-pill ${isRegistered ? "status-paid" : "status-unpaid"}`}>
                          {isRegistered ? "Paid" : "Unpaid"}
                        </span>
                        <span className={`status-pill ${isActive ? "status-active" : "status-inactive"}`}>
                          {isActive ? "Active" : "Deleted"}
                        </span>
                      </div>
                    </div>

                    <div className="user-mobile-grid">
                      <p><strong>Email:</strong> {user.email || "N/A"}</p>
                      <p><strong>Codice Fiscal:</strong> {user.codice_fiscale || "N/A"}</p>
                      <p><strong>Phone:</strong> {user.phone || "N/A"}</p>
                      <p><strong>Registration:</strong> {isRegistered ? "Paid" : "Unpaid"}</p>
                      <p><strong>Account:</strong> {isActive ? "Active" : "Deleted"}</p>
                    </div>

                    <div className="user-mobile-actions">
                      <button
                        type="button"
                        className={`user-status-btn ${isActive ? "status-delete" : "status-restore"}`}
                        onClick={() => handleToggleActive(user)}
                      >
                        {isActive ? "Delete" : "Restore"}
                      </button>
                      <button className="edit-btn" onClick={() => handleEdit(user, "user")}>
                        Edit User
                      </button>
                      {isRegistered && (
                        <button className="open-btn" onClick={() => handleViewDetails(user)}>
                          View Details
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
            {paginatedUsers.length === 0 && (
              <div className="payment-empty-state">
                <p>No users found for current filters.</p>
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
  <div className="pagination">
    <button
      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
      disabled={currentPage === 1}
    >
      Prev
    </button>

    {[...Array(totalPages)].map((_, idx) => {
      const pageNum = idx + 1;
      return (
        <button
          key={pageNum}
          className={currentPage === pageNum ? "active" : ""}
          onClick={() => setCurrentPage(pageNum)}
        >
          {pageNum}
        </button>
      );
    })}

    <button
      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
      disabled={currentPage === totalPages}
    >
      Next
    </button>
  </div>
)}
          </div>
        </>
      ) : (        <>
          <div className="top-bar">
            <button onClick={handleBack} className="back-btn">⬅ Back</button>
            <h1>📄 Registration & Payment Details</h1>
          </div>

          <div className="chapter-table-wrapper">
            <table className="chapter-table">
              <thead>
                <tr>
                  <th>Registration: Is Enabled</th>
                  <th>Registration: Payment Status</th>
                  <th>Registration: Expiry Date</th>
                  <th>codice_fiscale</th>
                  <th>Payment: Amount</th>
                  <th>Payment: Gateway</th>
                  <th>Payment: Status</th>
                  <th>Payment: Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedUser.registrations.length > 0 ? (
                  selectedUser.registrations.map((registration, index) => {
                    const payment = selectedUser.payments[index];
                    return (
                      <tr key={registration.id}>
                        <td>{registration.is_enabled ? "Enabled" : "Disabled"}</td>
                        <td>{registration.payment_status ? "Paid" : "Unpaid"}</td>
                        <td>{formatDate(registration.expiry_date)}</td>
                        <td>{selectedUser.codice_fiscale}</td>
                        {payment ? (
                          <>
                            <td>{formatEuroAmount(payment.amount)}</td>
                            <td>{payment.payment_gateway}</td>
                            <td>{payment.status}</td>
                            <td>{formatDate(payment.created_at)}</td>
                          </>
                        ) : (
                          <td colSpan={4} style={{ backgroundColor: "#d1ecf1", fontWeight: "bold" }}>
                            Payment has not been initiated.
                          </td>
                        )}
                        <td>
                          <button
                            className="edit-btn"
                            onClick={() => handleEdit(registration, "registration")}
                          >
                            Edit Registration
                          </button>
                          <button
                            className="open-btn"
                            onClick={() => handleViewPaymentDetails(selectedUser)}
                          >
                            View Payment Details
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" style={{ backgroundColor: "#f8d7da", fontWeight: "bold" }}>
                      No registration found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Edit Modal */}
      {isModalOpen && editItem && (
        <UserModal
          item={editItem}
          onClose={handleModalClose}
          onSave={handleSaveEdit}
          type={editType}
        />
      )}

      {/* Payment Details Modal */}
      {isPaymentModalOpen && selectedPayments && (
        <PaymentDetailsModal
          user={selectedPayments}
          onClose={handlePaymentModalClose}
        />
      )}
    </div>
  );
};

const UserModal = ({ item, onClose, onSave, type }) => {
  const [formData, setFormData] = useState({ ...item });
   // Decide which fields to show based on the edit type

   const fieldFilter = () => {

   if (type === "registration") {

      return ["is_enabled", "payment_status"];

    } else if (type === "user") {

      return ["full_name", "phone"];

    } else {

      return Object.keys(formData); // fallback

    }

  };
  const fieldsToEdit = fieldFilter();
  const modalTitle = `Edit ${type.charAt(0).toUpperCase() + type.slice(1)} Details`;

  const handleChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    let val = value;
    // Handle checkbox boolean fields properly
    if (inputType === "checkbox") {
      val = checked;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: val,
    }));
  };

  const handleSubmit = () => {
    onSave(formData);
  };

  const prettifyLabel = (key) => {
    return key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getBooleanLabel = (key, value) => {
    if (key === "is_active") return value ? "Active" : "Deleted";
    return value ? "Enabled" : "Disabled";
  };

  return (
    <div className="modal" onClick={onClose}>
      <div className="modal-content user-edit-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose} aria-label="Close modal">
          x
        </button>
        <div className="user-edit-header">
          <h2>{modalTitle}</h2>
        </div>

        <div className="user-edit-form">
          {fieldsToEdit.map((key) => {
            const isBoolean = typeof formData[key] === "boolean";

            return (
              <div key={key} className="user-form-field">
                <label className="user-form-label" htmlFor={`field-${key}`}>
                  {prettifyLabel(key)}
                </label>
                {isBoolean ? (
                  <div className="user-switch-wrap">
                    <input
                      id={`field-${key}`}
                      className="user-form-switch"
                      type="checkbox"
                      name={key}
                      checked={formData[key]}
                      onChange={handleChange}
                    />
                    <label className="user-switch-label" htmlFor={`field-${key}`}>
                      <span className="user-switch-slider" />
                      <span className="user-switch-text">
                        {getBooleanLabel(key, formData[key])}
                      </span>
                    </label>
                  </div>
                ) : (
                  <input
                    id={`field-${key}`}
                    className="user-form-input"
                    type="text"
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    placeholder={prettifyLabel(key)}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="modal-actions">
          <button onClick={handleSubmit} className="save-btn" type="button">
            Save
          </button>
          <button onClick={onClose} className="cancel-btn" type="button">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const PaymentDetailsModal = ({ user, onClose }) => {
  const allPayments = user.payments || [];
  // only show payments that have succeeded or failed; ignore pending ones entirely
  const filteredPayments = allPayments.filter(
    (payment) => {
      const st = payment.status?.toLowerCase();
      return st === "success" || st === "failed";
    }
  );

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
        return '#28a745';
      case 'failed':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  return (
    <div className="modal" onClick={onClose}>
      <div className="modal-content payment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="payment-modal-header">
          <h2>Payment Details</h2>
        </div>
        <button className="modal-close-btn" onClick={onClose} aria-label="Close modal">
          x
        </button>

        <div className="payment-user-summary">
          <h3>User Information</h3>
          <p><strong>Name:</strong> {user.full_name}</p>
          <p><strong>Email:</strong> {user.email}</p>
          <p><strong>Codice Fiscale:</strong> {user.codice_fiscale}</p>
        </div>

        <h3 className="payment-history-title">Payment History</h3>

        {/* pending payments are no longer shown or counted */}

        {filteredPayments.length > 0 ? (
          <div className="payment-list">
            {filteredPayments.map((payment, index) => (
              <div key={payment.id} className="payment-card">
                <div className="payment-card-head">
                  <h4>Payment #{index + 1}</h4>
                  <span
                    className="payment-status-badge"
                    style={{ backgroundColor: getStatusColor(payment.status) }}
                  >
                    {payment.status}
                  </span>
                </div>

                <div className="payment-grid">
                  <div>
                    <p className="payment-label"><strong>Amount:</strong></p>
                    <p className="payment-value payment-amount">{formatEuroAmount(payment.amount)}</p>
                  </div>

                  <div>
                    <p className="payment-label"><strong>Payment Gateway:</strong></p>
                    <p className="payment-value">{payment.payment_gateway}</p>
                  </div>

                  <div>
                    <p className="payment-label"><strong>Transaction ID:</strong></p>
                    <p className="payment-value payment-transaction">
                      {payment.transaction_id || 'N/A'}
                    </p>
                  </div>

                  <div>
                    <p className="payment-label"><strong>Created At:</strong></p>
                    <p className="payment-value">
                      {formatDateTime(payment.created_at)}
                    </p>
                  </div>

                  <div className="payment-address-row">
                    <p className="payment-label"><strong>Payment Address:</strong></p>
                    <p className="payment-value payment-transaction">{payment.payment_address}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="payment-empty-state">
            <p>No payment records found for this user.</p>
          </div>
        )}

        <div className="modal-actions payment-modal-actions">
          <button onClick={onClose} className="cancel-btn">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
